import { InvoiceStatus, type Currency, type Invoice } from '@etiquetachef/types';

/** Total em aberto de UMA moeda, em centavos. */
export type CurrencyTotal = { readonly currency: Currency; readonly amount: number };

export type OverviewStats = {
  /** `due_at` (ms) da fatura em aberto que vence primeiro; `null` sem pendência. */
  readonly nextDue: number | null;
  readonly nextDueAmount: number;
  readonly nextDueCurrency: Currency | null;
  /** Um total por moeda, só as com valor > 0, em ordem alfabética da moeda. */
  readonly totals: readonly CurrencyTotal[];
};

/**
 * Deriva a próxima fatura e o total em aberto POR MOEDA das faturas
 * PENDING/OVERDUE de um tenant.
 *
 * Origem: #935 (item 3) — 2026-10-13 10:33 +0200; a conta vivia inline em
 * `routes/BillingOverviewPage.tsx` desde ae81f4d21 (2026-05-05).
 *
 * Por quê: até 2026-10-13 o total somava `amount_due` de todas as faturas em
 * aberto e exibia na moeda da primeira — fatura em BRL e em EUR viravam um
 * número só, numa moeda só. Agora cada moeda tem o seu total; nunca somar
 * moedas diferentes (não há câmbio no admin, e não deveria haver aqui).
 *
 * Contrato: função pura. `amount_due` em CENTAVOS, na moeda da própria fatura.
 *
 * Usado por: `routes/BillingOverviewPage.tsx` (rota `/billing/overview/:tenantId`).
 * Teste: `overviewStats.test.ts`.
 */
export function overviewStats(invoices: readonly Invoice[]): OverviewStats {
  let nextDue: number | null = null;
  let nextDueAmount = 0;
  let nextDueCurrency: Currency | null = null;
  const byCurrency = new Map<Currency, number>();
  for (const inv of invoices) {
    if (inv.status !== InvoiceStatus.PENDING && inv.status !== InvoiceStatus.OVERDUE) continue;
    byCurrency.set(inv.currency, (byCurrency.get(inv.currency) ?? 0) + inv.amount_due);
    if (nextDue === null || inv.due_at < nextDue) {
      nextDue = inv.due_at;
      nextDueAmount = inv.amount_due;
      nextDueCurrency = inv.currency;
    }
  }
  const totals = [...byCurrency.entries()]
    .filter(([, amount]) => amount > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) => ({ currency, amount }));
  return { nextDue, nextDueAmount, nextDueCurrency, totals };
}
